import React from 'react';
import type { Driver } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { DriverIcon } from '@hero/web-icons';
import { useDriverDrawerStore } from '@hero/hooks-shared';
import { TableCell } from '../../../ui/Table/Table';
import { MianAvatar } from '../../../app/MianAvatar';
import { CellAction } from '../../../app/Table/CellAction';
import {
  AvailabilityChip,
  formatPlateForDisplay,
  driverStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export interface DriverRowProps {
  driver: Driver;
}

export const DriverRow: React.FC<DriverRowProps> = ({ driver }) => {
  const { t } = useTranslation(['m-driver', 'm-vehicle']);
  const openDrawer = useDriverDrawerStore(s => s.open);
  const handleViewClick = () => openDrawer(String(driver._id));

  const status = driver.status;
  const vehicleInfo = driver.vehicle;
  const chip = driverStatusChip(t, status);

  const typeLabel = vehicleInfo?.type
    ? t(`m-vehicle:types.${vehicleInfo.type}`, { defaultValue: vehicleInfo.type })
    : '';

  return (
    <>
      <TableCell className="text-gray-600 body2">
        <div className="flex items-center gap-3">
          <MianAvatar
            src=""
            className="size-[44px]"
            emptyFallback={
              <div className="flex size-full items-center justify-center rounded-full border border-gray-200 bg-primary/10">
                <DriverIcon className="size-6 text-primary" size="small" />
              </div>
            }
          />
          <div className="flex min-w-0 flex-col gap-0.5 text-start">
            <div className="text-sm font-medium text-gray-900">{driver.name}</div>
            <div className="text-xs text-muted-foreground">
              <span dir="ltr">{driver.phone || ''}</span>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {chip ? (
          <AvailabilityChip label={chip.label} tone={chip.tone} />
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-gray-600 body2">
        {vehicleInfo ? (
          <div className="flex min-w-0 flex-col gap-0.5 text-start">
            {typeLabel ? (
              <>
                <span className="truncate text-sm font-medium text-gray-900">{typeLabel}</span>
                <span className="truncate text-xs text-muted-foreground">
                  <span dir="auto">{formatPlateForDisplay(vehicleInfo.plate) || '—'}</span>
                </span>
              </>
            ) : (
              <span className="truncate text-sm font-medium text-gray-900">
                <span dir="auto">{formatPlateForDisplay(vehicleInfo.plate) || '—'}</span>
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm font-medium text-red-500">{t('table.no_vehicle_assigned')}</span>
        )}
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <CellAction type="view" onClick={handleViewClick} />
      </TableCell>
    </>
  );
};
